import { useMemo } from "react";
import { Alert, Container } from "@simplybusiness/mobius";
import {
  type QuestionnaireProps,
  Questionnaire,
} from "../../components/Questionnaire";
import {
  getReactInterventionComponent,
  InterventionWrapper,
} from "@simplybusiness/mobius-interventions";
import {
  getInsuranceAdvisorData,
  InsuranceAdvisorProvider,
} from "../../contexts";
import { getPropsByName } from "../../utils";
import { Page, PageProviders } from "../Page";
import { useSetQuestionnaireFormUrl } from "../../hooks/useQuestionnaireFormUrl";
import type {
  AlertProps,
  PageProps,
  WithMetadata,
  Action,
} from "../Page/types";
import type { QuestionnairePageProps } from "./types";
import { useExperiment } from "../../hooks/experiments";
import { useQuestionValidationTracking } from "../../features/questionnaire/hooks/useQuestionValidationTracking";

const QuestionnairePageInner = (props: QuestionnairePageProps) => {
  const {
    layout: { components },
  } = props;

  // uk-26-01-shop-corr-address-changes: change address sync config based on experiment variant
  // added in ahoy-4010-add-experiment-address-sync: on cleanup remove both consts
  const variant = useExperiment("uk2601ShopCorrAddressChanges");

  useQuestionValidationTracking();
  const addressSync = useMemo(
    () =>
      variant === "with-risk-address"
        ? {
            linkQuestionName: "Same correspondence address pre quote shop q2q",
            linkQuestionValue: "yes",
            linkedQuestionName: "UK correspondence address lookup shop q2q",
            syncMap: {
              "UK address lookup": "UK correspondence address lookup shop q2q",
              address_1: "customer[address_1]",
              address_2: "customer[address_2]",
              town: "customer[town]",
              uk_postcode: "customer[uk_postcode]",
            },
          }
        : undefined,
    [variant],
  );

  // Split state into individual components
  const alert = getPropsByName({ name: "alert", components });
  const questionnaire = getPropsByName({
    name: "questionnaire",
    components,
  }) as WithMetadata<QuestionnaireProps>;

  const submitAction = useMemo(
    () =>
      questionnaire.actions?.find(
        (action: Action) => action.actionType === "submit",
      ),
    [questionnaire.actions],
  );
  useSetQuestionnaireFormUrl(submitAction?.url);

  // uk-26-01-shop-corr-address-changes: Update the questionnaire props to include new address sync experiment config
  // added in ahoy-4010-add-experiment-address-sync: on cleanup remove function
  const questionnaireWithConfig = useMemo(
    () => ({
      ...questionnaire,
      config: {
        ...questionnaire.config,
        addressSync,
      },
    }),
    [questionnaire, addressSync],
  );

  // uk-26-01-shop-corr-address-changes: on cleanup revert questionnaireWithConfig back to questionnaire
  const { interventions = [], ...questionnaireProps } = questionnaireWithConfig;

  return (
    <Container size="sm">
      {interventions.map((intervention: string) => {
        const interventionElement = getReactInterventionComponent(intervention);
        if (interventionElement) {
          return (
            <InterventionWrapper key={intervention} name={intervention}>
              {interventionElement}
            </InterventionWrapper>
          );
        }
        return null;
      })}
      {!!alert &&
        (() => {
          const { type, header, textContent } =
            alert as WithMetadata<AlertProps>;
          return (
            <Alert variant={type} header={header}>
              {textContent}
            </Alert>
          );
        })()}
      {!!questionnaire && <Questionnaire {...questionnaireProps} />}
    </Container>
  );
};

export const QuestionnairePage = (props: QuestionnairePageProps) => {
  const insuranceAdvisorData = getInsuranceAdvisorData(props as PageProps);

  return (
    <PageProviders {...(props as PageProps)}>
      <InsuranceAdvisorProvider insuranceAdvisorData={insuranceAdvisorData}>
        <Page {...(props as PageProps)}>
          <main className="mobius mobius-page__main">
            <QuestionnairePageInner {...props} />
          </main>
        </Page>
      </InsuranceAdvisorProvider>
    </PageProviders>
  );
};
